import React, { useEffect } from 'react';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { CloseIcon, CheckIcon, AlertCircleIcon, InfoIcon } from 'components/icons';
import styles from './alert.module.scss';

interface AlertProps {
  message?: string | null;
  type?: 'success' | 'error' | 'info';
  icon?: React.ReactNode;
  className?: string; // Allow custom overrides
  onClose?: () => void;
  variant?: 'local' | 'toast';
}

const getDefaultIcon = (type: 'success' | 'error' | 'info'): React.ReactNode => {
  switch (type) {
    case 'success':
      return <CheckIcon width="18" height="18" />;
    case 'error':
      return <AlertCircleIcon width="18" height="18" />;
    case 'info':
    default:
      return <InfoIcon width="18" height="18" />;
  }
};

export const Alert: React.FC<AlertProps> = ({
  message,
  type = 'info',
  icon,
  className = '',
  onClose,
  variant = 'toast',
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (variant === 'toast' && message) {
      dispatch(
        showToast({
          text: message,
          type,
        }),
      );
      if (onClose) {
        onClose();
      }
    }
  }, [message, type, dispatch, onClose, variant]);

  if (variant === 'toast') {
    return null;
  }

  if (!message) return null;

  const resolvedIcon = icon !== undefined ? icon : getDefaultIcon(type);

  return (
    <div className={`${styles.alert} ${styles[type]} ${className}`}>
      {resolvedIcon && <div className={styles.icon}>{resolvedIcon}</div>}
      <span className={styles.message}>{message}</span>
      {onClose && (
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close alert">
          <CloseIcon width="16" height="16" />
        </button>
      )}
    </div>
  );
};
